// The outbox (plan §6.4): local operations queue here and flush to the server
// in order. Sending is idempotent on (client id, sequence number), so a retry
// after a lost reply is safe, and consecutive typing in one paragraph
// collapses to one operation before it is sent, which keeps the operation log
// the size of the edits rather than the size of the keystrokes.
//
// The queue is written to IndexedDB as it grows, so a tab closed or evicted
// with unsent work loses nothing: reopening the document finds them and sends
// them. Sequence numbers must never repeat for one (client, document) pair,
// across visits as well as within one, because the server skips a sequence it
// has already applied.

import { ApiError, OfflineError, api } from "../api/client";
import { dequeue, enqueue, loadQueue, replaceQueued } from "../storage/cache";
import type { Op } from "../store/ops";

export type SyncStatus = "idle" | "syncing" | "offline" | "conflict";

interface Pending {
  seq: number;
  op: Op;
}

const FLUSH_DELAY_MS = 400;
const MAX_BACKOFF_MS = 30_000;

export class Outbox {
  readonly clientId: string;
  private pending: Pending[] = [];
  private seq: number;
  private inflight = false;
  private timer: number | null = null;
  private backoff = 1000;
  private _status: SyncStatus = "idle";
  private listeners = new Set<() => void>();

  constructor(
    private readonly documentId: string,
    private rev: number,
    private readonly onConflict: () => void,
  ) {
    this.clientId = clientId();
    this.seq = nextSequenceStart(this.clientId, documentId);
  }

  /** Adopt operations left over from an earlier visit. */
  adopt(queued: Pending[]): void {
    if (queued.length === 0) return;
    this.pending = [...queued, ...this.pending];
    this.seq = Math.max(this.seq, ...queued.map((p) => p.seq));
    this.schedule(FLUSH_DELAY_MS);
  }

  get status(): SyncStatus {
    return this._status;
  }

  get queued(): number {
    return this.pending.length;
  }

  /** Operations this device still owes the server, in order. Replaying these
   *  onto the server's version reconstructs what the author should see. */
  pendingOps(): Op[] {
    return this.pending.map((p) => p.op);
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): SyncStatus => this._status;

  push(op: Op): void {
    const last = this.pending[this.pending.length - 1];
    const mergeable =
      last &&
      ((op.t === "set_text" && last.op.t === "set_text" && last.op.id === op.id) ||
        (op.t === "set_title" && last.op.t === "set_title"));
    if (mergeable && last) {
      last.op = op;
      void replaceQueued(this.documentId, last.seq, op);
    } else {
      this.seq += 1;
      rememberSequence(this.clientId, this.documentId, this.seq);
      this.pending.push({ seq: this.seq, op });
      void enqueue(this.documentId, this.seq, op);
    }
    this.schedule(FLUSH_DELAY_MS);
  }

  setRev(rev: number): void {
    this.rev = Math.max(this.rev, rev);
  }

  /** Throw away what has not been sent, here and on disk.
   *
   *  Only for a restore (design §13): the document has deliberately been
   *  replaced by an older one, so replaying edits made against the newer text
   *  would put back a little of what the author just asked to leave behind.
   *  Everything that had reached the server is in the version they restored
   *  from or in the one the restore appended; nothing silently disappears. */
  discard(): void {
    const dropped = this.pending;
    this.pending = [];
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
    if (dropped.length > 0) {
      void dequeue(
        this.documentId,
        dropped.map((p) => p.seq),
      );
    }
    this.setStatus("idle");
  }

  private schedule(delay: number): void {
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, delay);
  }

  async flush(keepalive = false): Promise<void> {
    if (this.inflight || this.pending.length === 0) return;
    this.inflight = true;
    const batch = this.pending;
    this.pending = [];
    this.setStatus("syncing");
    try {
      const result = await api.post<{ rev: number }>(
        `/api/documents/${this.documentId}/ops`,
        {
          client_id: this.clientId,
          base_rev: this.rev,
          seqs: batch.map((p) => p.seq),
          ops: batch.map((p) => p.op),
        },
        keepalive,
      );
      this.rev = result.rev;
      this.backoff = 1000;
      void dequeue(
        this.documentId,
        batch.map((p) => p.seq),
      );
      this.setStatus("idle");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // The document moved under us in a way these operations cannot
        // survive. They are dropped, here and on disk; the screen reloads the
        // server's picture.
        void dequeue(
          this.documentId,
          batch.map((p) => p.seq),
        );
        this.setStatus("conflict");
        this.onConflict();
      } else {
        // Network trouble, a signed-out session, or a server error: keep
        // everything and try again. Nothing is dropped, and it is on disk.
        this.pending = [...batch, ...this.pending];
        this.setStatus(e instanceof OfflineError ? "offline" : "syncing");
        this.schedule(this.backoff);
        this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
      }
    } finally {
      this.inflight = false;
      if (this.pending.length > 0 && this._status === "idle") this.schedule(FLUSH_DELAY_MS);
    }
  }

  private setStatus(s: SyncStatus): void {
    if (s === this._status) return;
    this._status = s;
    for (const l of this.listeners) l();
  }
}

/** Operations this device still owes the server for a document. */
export async function pendingFor(documentId: string): Promise<Pending[]> {
  const rows = await loadQueue(documentId);
  return rows.map((r) => ({ seq: r.seq, op: r.op }));
}

function clientId(): string {
  const key = "metis.client_id";
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const made = crypto.randomUUID();
    localStorage.setItem(key, made);
    return made;
  } catch {
    return crypto.randomUUID();
  }
}

function sequenceKey(id: string, documentId: string): string {
  return `metis.seq.${id}.${documentId}`;
}

/** A starting point strictly above anything this browser has used for the
 *  document before, and above the current time in milliseconds, so a second
 *  browser profile sharing a client id by accident still cannot collide. */
export function nextSequenceStart(id: string, documentId: string): number {
  let last = 0;
  try {
    last = Number(localStorage.getItem(sequenceKey(id, documentId)) ?? 0) || 0;
  } catch {
    // no storage: the clock alone still moves forward between visits
  }
  return Math.max(last, Date.now());
}

function rememberSequence(id: string, documentId: string, seq: number): void {
  try {
    localStorage.setItem(sequenceKey(id, documentId), String(seq));
  } catch {
    // best effort; the clock floor covers the next visit
  }
}
